import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDown, CalendarCheck, ClipboardCheck, Phone } from "lucide-react";
import { PHONE, TEL, scrollToId } from "./data";

const ease = [0.16, 1, 0.3, 1];
const LINES = ["Junk", "Removal", "Greenville SC"];

const MaskLine = ({ children, i, className = "" }) => (
  <span className="block overflow-hidden pb-[0.04em]">
    <motion.span className={`block ${className}`} initial={{ y: "105%" }} animate={{ y: "0%" }} transition={{ duration: 1.1, delay: 0.25 + i * 0.12, ease }}>
      {children}
    </motion.span>
  </span>
);

const Badge = ({ icon: Icon, label, testid }) => (
  <div data-testid={testid} className="flex items-center gap-2.5 rounded-full border border-white/15 bg-white/5 py-2 pl-2 pr-4 text-sm font-bold text-[#F2EEE6]">
    <span className="grid h-7 w-7 place-items-center rounded-full bg-[#FFB000] text-[#0D0D0B]"><Icon className="h-4 w-4" strokeWidth={2.5} /></span>
    {label}
  </div>
);

const TiltPhoto = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 18 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "8%"]);
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  return (
    <motion.div ref={ref} initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.4, delay: 0.5, ease }} className="relative [perspective:1200px]" onPointerMove={onMove} onPointerLeave={() => { mx.set(0); my.set(0); }}>
      <motion.div style={{ rotateX: rx, rotateY: ry }} className="relative aspect-[3/4] w-full overflow-hidden rounded-[28px] bg-[#191915] [clip-path:polygon(0_0,100%_0,100%_88%,86%_100%,0_100%)] [transform-style:preserve-3d]">
        <motion.img style={{ y: imgY }} src="/img/cazares-junk-removal-trailer-brick-haul-greenville-sc.webp" fetchpriority="high" width="480" height="640" alt="Cazares Junk Removal trailer loaded with old brick and cinder blocks at a Greenville SC home" className="absolute inset-0 h-[110%] w-full -translate-y-[5%] object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0B]/85 via-transparent to-transparent" />
        <div className="absolute bottom-8 left-6 right-16 sm:bottom-10 sm:left-8">
          <p className="font-display text-5xl uppercase leading-[0.9] text-[#F2EEE6] sm:text-6xl">Point.<br />We haul. <span className="text-[#FFB000]">Done.</span></p>
        </div>
      </motion.div>
      <div className="spin-slow absolute -right-3 -top-6 hidden h-28 w-28 sm:block" aria-hidden="true">
        <svg viewBox="0 0 100 100" className="h-full w-full">
          <defs><path id="circ" d="M50,50 m-38,0 a38,38 0 1,1 76,0 a38,38 0 1,1 -76,0" /></defs>
          <circle cx="50" cy="50" r="49" fill="#FFB000" />
          <text fontSize="10" fontWeight="800" letterSpacing="1.6" fill="#0D0D0B"><textPath href="#circ" textLength="236">FREE QUOTE • SAME DAY • UPFRONT •</textPath></text>
        </svg>
      </div>
    </motion.div>
  );
};

export const Hero = () => (
  <section id="top" data-testid="hero-section" className="relative overflow-hidden bg-[#0D0D0B] pb-20 pt-32 lg:pb-28">
    <div className="pointer-events-none absolute -left-40 top-20 h-[520px] w-[520px] rounded-full bg-[#FFB000]/10 blur-[120px]" />
    <div className="relative mx-auto grid max-w-7xl gap-14 px-4 sm:px-6 lg:grid-cols-12 lg:gap-10">
      <div className="lg:col-span-7 lg:pt-6">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1, duration: 0.8 }} className="mb-6 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.28em] text-[#FFB000]">
          <span className="relative flex h-2.5 w-2.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#FFB000] opacity-75" /><span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#FFB000]" /></span>
          Cazares Junk Removal · Upstate SC
        </motion.p>
        <h1 data-testid="hero-headline" className="font-display text-[20vw] uppercase leading-[0.86] text-[#F2EEE6] sm:text-[7.5rem] lg:text-[7.5rem] xl:text-[8.75rem]">
          {LINES.map((l, i) => (
            <MaskLine key={l} i={i} className={i === 2 ? "text-[#FFB000]" : ""}>{l}</MaskLine>
          ))}
        </h1>
        <motion.p data-testid="hero-subhead" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8, duration: 0.8, ease }} className="mt-7 max-w-md text-xl font-semibold text-[#F2EEE6] sm:text-2xl">
          Fast, Friendly, Upfront Pricing
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.95, duration: 0.8, ease }} className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
          <motion.a whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} data-testid="hero-call-now-button" href={TEL} className="cta-glow group flex items-center justify-center gap-4 rounded-full bg-[#FFB000] py-5 pl-5 pr-8 text-[#0D0D0B]">
            <span className="grid h-12 w-12 place-items-center rounded-full bg-[#0D0D0B] text-[#FFB000] transition-transform duration-500 group-hover:rotate-[20deg]"><Phone className="h-5 w-5" strokeWidth={2.75} /></span>
            <span className="text-left leading-tight">
              <span className="block text-2xl font-black uppercase tracking-tight">Call Now</span>
              <span className="block text-sm font-bold tabular-nums">{PHONE}</span>
            </span>
          </motion.a>
          <button data-testid="hero-instant-quote-button" onClick={() => scrollToId("quote")} className="group flex items-center justify-center gap-2 rounded-full border border-white/25 px-7 py-5 font-bold text-[#F2EEE6] transition-colors hover:border-[#FFB000] hover:text-[#FFB000]">
            Request a Quote <ArrowDown className="h-4 w-4 transition-transform group-hover:translate-y-1" />
          </button>
        </motion.div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.15, duration: 0.8 }} className="mt-8 flex flex-wrap gap-3">
          <Badge icon={ClipboardCheck} label="Free Onsite Quote" testid="hero-trust-badge-onsite" />
          <Badge icon={CalendarCheck} label="Same Day Pickup" testid="hero-trust-badge-sameday" />
        </motion.div>
      </div>
      <div className="mx-auto w-full max-w-md lg:col-span-5 lg:max-w-none">
        <TiltPhoto />
      </div>
    </div>
  </section>
);
